import { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

export default function WheelPicker({ value, onChange, min = 1, max = 50, label = "Ordem" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredValue, setHoveredValue] = useState(null);
  const containerRef = useRef(null);
  const scrollRef = useRef(null);

  // Fechar ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll para o valor selecionado quando abrir
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      const selectedElement = scrollRef.current.querySelector(`[data-value="${value + 1}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [isOpen, value]);

  const handleSelect = (newValue) => {
    onChange(newValue - 1); // Converte display (1-50) para index (0-49)
    setIsOpen(false);
  };

  const displayValue = value + 1; // Converte index (0-49) para display (1-50)

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-semibold text-text-primary mb-2">
        {label}
      </label>
      
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary flex items-center justify-between hover:border-gray-600"
      >
        <span className="font-semibold text-lg">{displayValue}</span>
        <div className="flex flex-col gap-0.5">
          <ChevronUp size={14} className={`transition-colors ${isOpen ? 'text-primary-500' : 'text-gray-500'}`} />
          <ChevronDown size={14} className={`transition-colors ${isOpen ? 'text-primary-500' : 'text-gray-500'}`} />
        </div>
      </button>

      <p className="text-xs text-gray-500 mt-2">
        Define a ordem em que aparece para os usuários (1 = primeiro)
      </p>

      {/* Dropdown Wheel */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-background-800 border-2 border-primary-500 rounded-2xl shadow-2xl shadow-primary-500/20 overflow-hidden">
          {/* Scroll Container */}
          <div 
            ref={scrollRef}
            className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-primary-500 scrollbar-track-background-700"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #374151'
            }}
          >
            {/* Gradient overlays */}
            <div className="sticky top-0 h-8 bg-gradient-to-b from-background-800 to-transparent pointer-events-none z-10" />
            
            {/* Options */}
            <div className="py-2">
              {[...Array(max - min + 1)].map((_, i) => {
                const optionValue = min + i;
                const isSelected = optionValue === displayValue;
                const isHovered = optionValue === hoveredValue;
                
                return (
                  <button
                    key={optionValue}
                    type="button"
                    data-value={optionValue}
                    onClick={() => handleSelect(optionValue)}
                    onMouseEnter={() => setHoveredValue(optionValue)}
                    onMouseLeave={() => setHoveredValue(null)}
                    className={`w-full px-6 py-3 text-center transition-all duration-150 ${
                      isSelected
                        ? 'bg-primary-500 text-white font-bold text-xl scale-105'
                        : isHovered
                        ? 'bg-primary-500/20 text-primary-400 font-semibold text-lg'
                        : 'text-text-secondary hover:text-text-primary text-base'
                    }`}
                  >
                    {optionValue}
                  </button>
                );
              })}
            </div>

            <div className="sticky bottom-0 h-8 bg-gradient-to-t from-background-800 to-transparent pointer-events-none" />
          </div>
        </div>
      )}
    </div>
  );
}
